"use client"
import React, { useEffect, useMemo, useState } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { IUser } from '@/modules/users/domain/IUser'
import { Loader, Pagination } from '@/shared/index'
import { IUsersTableProps } from '@/modules/users/domain/UsersTable'
import { calculateFullAge, getRoles } from '@/modules/users/app/utils/functions'
import {
	Table as TableComponent,
	TableBody,
	TableRow,
} from "src/shadcn/components/ui/table";
import Link from 'next/link'
import RoleEditor from './RoleEditor'
import DoctorDocumentsModal from './DoctorDocuments'
import ResetPasswordModal from './ResetPasswordModal'

const DOCTOR_ROLE = "doctor";
const ROL_SUPERVISOR_LINEA = 9;

/** "SAN MARTÍN" → "San Martín". */
const tituloDeLinea = (linea: string) =>
	linea
		.toLowerCase()
		.split(" ")
		.map((p, i) => (i && ["de", "la", "del"].includes(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)))
		.join(" ");

export default function UsersTable({users, usersLoading, filters, page, setPage, total, isAdmin, isSoporte, onRoleChange, onActiveChange, onEdit}: IUsersTableProps) {
  const canResetPassword = !!(isAdmin || isSoporte);
  // Mismo criterio que la API: ADMIN, OWNER y SOPORTE editan datos. Acá el
  // portal sólo distingue admin de soporte, y el owner entra por isAdmin.
  const canEdit = !!(isAdmin || isSoporte) && !!onEdit;
  // Sólo un admin da de baja: es una acción que saca a alguien de circulación.
  const canDeactivate = !!isAdmin;
  const [togglingUid, setTogglingUid] = useState<string | null>(null);

  const toggleActive = async (uid: string, next: boolean) => {
    if (!onActiveChange || togglingUid) return;
    setTogglingUid(uid);
    try {
      await onActiveChange(uid, next);
    } finally {
      setTogglingUid(null);
    }
  };
  const [docModalUser, setDocModalUser] = useState<{ uid: string; name: string } | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<{ uid: string; fullName: string; email: string | null | undefined } | null>(null);

  const columns = useMemo<ColumnDef<IUser>[]>(() => [
	{
		accessorKey: "nomina",
		header: () => <span>Nomina</span>,
	},
	{
		accessorKey: "firstName",
		header: () => <span>Nombres</span>,
	},
	{
		accessorKey: "lastName",
		header: () => <span>Apellidos</span>,
	},
	{
		accessorKey: "dob",
		header: () => <span>Edad</span>,
		cell: ({ row: { original } }) => {
			return calculateFullAge(original.dob);
		},
	},
	{
		accessorKey: "sex",
		header: () => <span>Sexo</span>,
	},
	{
		// El cargo unificado que trae el padrón de Trenes. Es lo que después
		// agrupa la carátula, así que verlo acá ayuda a detectar uno mal
		// cargado antes de que salga en un informe.
		accessorKey: "position",
		header: () => <span>Cargo</span>,
		cell: ({ row: { original } }) => original.position ?? "—",
	},
	{
		accessorKey: "dt_create",
		header: () => <span>Alta</span>,
		cell: ({ row: { original } }) => {
			if (!original.dt_create) return "—";
			return new Date(original.dt_create).toLocaleDateString("es-AR", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			});
		},
	},
	{
		accessorKey: "active",
		header: () => <span>Estado</span>,
		cell: ({ row: { original } }) =>
			original.active === false ? (
				<span className="text-[10px] px-2 py-0.5 rounded-full bg-grey-100 text-grey-600 whitespace-nowrap">
					Inactivo
				</span>
			) : (
				<span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 whitespace-nowrap">
					Activo
				</span>
			),
	},
	{
		accessorKey: "email",
		header: () => <span>Email</span>,
	},
	{
		accessorKey: "phone",
		header: () => <span>Telefono</span>,
	},
	{
		accessorKey: "roles",
		header: () => <span>Roles</span>,
		cell: ({ row: { original } }) => {
			const roles =
				isAdmin && onRoleChange ? (
					<RoleEditor
						uid={original.uid}
						currentRoles={original.roles}
						onRoleChange={onRoleChange}
					/>
				) : (
					getRoles(original.roles)
				);
			// Al supervisor de línea se le ven sus líneas al lado del rol: sin
			// líneas no ve nada, y eso tiene que saltar a la vista.
			if (!original.roles?.some((r) => r.id === ROL_SUPERVISOR_LINEA)) {
				return roles;
			}
			const lineas = original.lineas?.map((l) => l.linea) ?? [];
			return (
				<div className="flex flex-col gap-0.5">
					{roles}
					<span
						className={`text-[11px] ${lineas.length ? "text-grey-500" : "text-orange-700"}`}
					>
						{lineas.length
							? `Líneas: ${lineas.map(tituloDeLinea).join(", ")}`
							: "Sin líneas asignadas"}
					</span>
				</div>
			);
		},
	},
	{
		accessorKey: "acciones",
		header: () => <span>Acciones</span>,
		cell: ({ row: { original } }) => {
			const isDoctor = original.roles?.some((r) => r.role?.toLowerCase() === DOCTOR_ROLE || r.id === 4);
			return (
				<div className="flex items-center gap-2">
					{canEdit && (
						<button
							type="button"
							title="Editar datos"
							aria-label={`Editar datos de ${original.firstName} ${original.lastName}`}
							onClick={() => onEdit?.(original.uid)}
							className="text-xs px-2 py-1 rounded-md border border-primary-700 text-primary-700 whitespace-nowrap"
						>
							Editar
						</button>
					)}
					<Link href={`/usuarios/${original.uid}/credencial`}>
						<svg
							width="32"
							height="32"
							viewBox="0 0 32 32"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M4.15 6C4.15 4.97827 4.97827 4.15 6 4.15H26C27.0217 4.15 27.85 4.97827 27.85 6V26C27.85 27.0217 27.0217 27.85 26 27.85H6C4.97827 27.85 4.15 27.0217 4.15 26V6Z"
								fill="#FCFCFC"
							/>
							<path
								d="M4.15 6C4.15 4.97827 4.97827 4.15 6 4.15H26C27.0217 4.15 27.85 4.97827 27.85 6V26C27.85 27.0217 27.0217 27.85 26 27.85H6C4.97827 27.85 4.15 27.0217 4.15 26V6Z"
								stroke="#FCFCFC"
								strokeWidth="0.3"
							/>
							<path
								d="M22.75 19H20.5C20.1022 19 19.7206 19.158 19.4393 19.4393C19.158 19.7206 19 20.1022 19 20.5V22.75M22.75 22.75V22.7575M16 12.25V14.5C16 14.8978 15.842 15.2794 15.5607 15.5607C15.2794 15.842 14.8978 16 14.5 16H12.25M9.25 16H9.2575M16 9.25H16.0075M16 19V19.0075M19 16H19.75M22.75 16V16.0075M16 22.75V22M10 9.25H12.25C12.6642 9.25 13 9.58579 13 10V12.25C13 12.6642 12.6642 13 12.25 13H10C9.58579 13 9.25 12.6642 9.25 12.25V10C9.25 9.58579 9.58579 9.25 10 9.25ZM19.75 9.25H22C22.4142 9.25 22.75 9.58579 22.75 10V12.25C22.75 12.6642 22.4142 13 22 13H19.75C19.3358 13 19 12.6642 19 12.25V10C19 9.58579 19.3358 9.25 19.75 9.25ZM10 19H12.25C12.6642 19 13 19.3358 13 19.75V22C13 22.4142 12.6642 22.75 12.25 22.75H10C9.58579 22.75 9.25 22.4142 9.25 22V19.75C9.25 19.3358 9.58579 19 10 19Z"
								stroke="#008EAC"
								strokeWidth="1.71429"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</Link>
					{isAdmin && isDoctor && (
						<button
							onClick={() =>
								setDocModalUser({
									uid: original.uid,
									name: `${original.firstName} ${original.lastName}`,
								})
							}
							className="text-[10px] px-2 py-1 bg-teal-50 text-teal-700 rounded hover:bg-teal-100 whitespace-nowrap"
							title="Ver documentos del medico"
						>
							Docs
						</button>
					)}
					{canDeactivate && (
						/* Baja lógica, nunca borrado: eliminar al usuario se
						   llevaría en cascada todo su historial de evaluaciones. */
						<button
							type="button"
							disabled={togglingUid === original.uid}
							onClick={() => {
								const next = original.active === false;
								const accion = next ? "rehabilitar" : "dar de baja a";
								if (
									window.confirm(
										`¿Querés ${accion} ${original.firstName} ${original.lastName}?\n\n` +
											(next
												? "Va a volver a aparecer al crear evaluaciones."
												: "Deja de aparecer al crear evaluaciones. Su historial se conserva."),
									)
								) {
									toggleActive(original.uid, next);
								}
							}}
							className="text-[10px] px-2 py-1 bg-grey-100 text-grey-700 rounded hover:bg-grey-200 whitespace-nowrap disabled:opacity-50"
							title={
								original.active === false
									? "Volver a habilitar este usuario"
									: "Dar de baja: deja de aparecer al crear evaluaciones, conserva su historial"
							}
						>
							{togglingUid === original.uid
								? "…"
								: original.active === false
									? "Rehabilitar"
									: "Dar de baja"}
						</button>
					)}
					{canResetPassword && (
						<button
							type="button"
							onClick={() =>
								setResetPasswordUser({
									uid: original.uid,
									fullName: `${original.firstName} ${original.lastName}`,
									email: original.email,
								})
							}
							className="text-[10px] px-2 py-1 bg-primary-50 text-primary-700 rounded hover:bg-primary-100 whitespace-nowrap"
							title="Setear una nueva contraseña para este usuario"
						>
							Reset pass
						</button>
					)}
				</div>
			);
		},
	},
  ], [isAdmin, canResetPassword, canDeactivate, canEdit, onRoleChange, onActiveChange, onEdit, togglingUid])

  const table = useReactTable({
    data: users,
    columns: columns,
    manualPagination: true,
    pageCount: Math.ceil(total / 15),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  })

  const [state, setState] = useState(table.initialState)
  table.setOptions(prev => ({
    ...prev,
    state,
    onStateChange: setState
  }))

 // When page number changes, update paginator counter
  useEffect(() => {
   table.setPageIndex(page - 1)
  }, [table, page])

  useEffect(() => {
   setPage(state.pagination.pageIndex + 1)
  }, [setPage, state.pagination.pageIndex])

  // if any filter changes, reset pagination to page 1
  useEffect(() => {
   table.setPageIndex(0)
  }, [table, filters])

  return (
		<div className="relative mb-12 flex flex-col h-full">
				<>
					<div
						className="rounded-xl overflow-scroll overflow-x-scroll relative max-h-full"
						style={{
							height: "100%",
							width: `100%`,
						}}
					>
						<TableComponent className="w-full h-min">
							<thead className="bg-[#F5F7F9] text-grey-900 font-semibold sticky top-0">
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<th
												key={header.id}
												colSpan={header.colSpan}
											>
												{header.isPlaceholder
													? null
													: flexRender(
															header.column
																.columnDef
																.header,
															header.getContext()
														)}
											</th>
										))}
									</TableRow>
								))}
							</thead>
							<TableBody className="last:mb-40">
								{table?.getRowModel()?.rows.map((row) => (
									<TableRow key={row.id}>
										{row.getVisibleCells().map((cell) => (
											<td
												className="p-4 w-auto max-w-xs h-10 min-h-[54px] max-h-[54px]"
												key={cell.id}
											>
												<div className="flex text-grey-400 flex-wrap align-middle justify-center">
													<p>
														{flexRender(
															cell.column
																.columnDef.cell,
															cell.getContext()
														)}
													</p>
												</div>
											</td>
										))}
									</TableRow>
								))}
							</TableBody>
						</TableComponent>
						<Pagination<IUser>
							table={table}
							isLoading={false}
						/>
					</div>
				</>
			{usersLoading && (
				<div className="absolute h-full w-full z-10 top-0 left-0 bg-white/70 flex justify-center items-center">
					<Loader />
				</div>
			)}
			{docModalUser && (
				<DoctorDocumentsModal
					uid={docModalUser.uid}
					doctorName={docModalUser.name}
					onClose={() => setDocModalUser(null)}
				/>
			)}
			{resetPasswordUser && (
				<ResetPasswordModal
					uid={resetPasswordUser.uid}
					fullName={resetPasswordUser.fullName}
					email={resetPasswordUser.email}
					onClose={() => setResetPasswordUser(null)}
				/>
			)}
		</div>
  );
}
